import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { calculateRiskScore } from '../utils/riskCalculator';

const SectionB = ({ assessmentData, setAssessmentData }) => {
  const navigate = useNavigate();
  const [organizationName, setOrganizationName] = useState(
    assessmentData.organizationName || assessmentData.companyName || ''
  );
  const [responses, setResponses] = useState(assessmentData.responses || {});
  const [currentSection, setCurrentSection] = useState('B');
  const [loading, setLoading] = useState(false);

  // Helper function to get value, defaulting to false (No) if undefined
  const getResponseValue = (key) => {
    return responses[key] !== undefined ? responses[key] : false;
  };

  const handleChange = (key, value) => {
    setResponses({ ...responses, [key]: value });
  };

  const handleCheckboxChange = (key, checked) => {
    setResponses({ ...responses, [key]: checked });
  };

  const handleSectionChange = async (section) => {
    // Auto-save logic to Firestore
    if (assessmentData.docId) {
      try {
        const normalizedResponses = {};
        Object.keys(responses).forEach(key => {
          normalizedResponses[key] = responses[key] !== undefined ? responses[key] : false;
        });
        
        await updateDoc(doc(db, 'assessments', assessmentData.docId), {
          organizationName: organizationName || assessmentData.companyName || '',
          responses: normalizedResponses,
          updatedAt: new Date()
        });
        
        // Update local context
        setAssessmentData(prev => ({
          ...prev,
          organizationName: organizationName || prev.companyName || '',
          responses: normalizedResponses
        }));
      } catch (err) {
        console.error("Error auto-saving", err);
      }
    }

    setCurrentSection(section);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Normalize responses: default all undefined values to false (No)
      const normalizedResponses = {};
      Object.keys(responses).forEach(key => {
        normalizedResponses[key] = responses[key] !== undefined ? responses[key] : false;
      });
      
      const { score, level } = calculateRiskScore(normalizedResponses, assessmentData.sectionA);

      if (assessmentData.docId) {
        await updateDoc(doc(db, 'assessments', assessmentData.docId), {
          organizationName: organizationName || assessmentData.companyName || '',
          responses: normalizedResponses,
          riskScore: score,
          riskLevel: level,
          updatedAt: new Date(),
          completed: true
        });
      }

      setAssessmentData({
        ...assessmentData,
        organizationName: organizationName || assessmentData.companyName || '',
        responses: normalizedResponses,
        riskScore: score,
        riskLevel: level
      });

      navigate('/results');
    } catch (error) {
      console.error('Error submitting assessment:', error);
      alert('Error submitting assessment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const SectionHeading = ({ id, subject, title, emWord }) => (
    <>
      <div className="section-title-eyebrow">
        <span>Section {id}</span>
        <span className="dot">◆</span>
        <span>{subject}</span>
      </div>
      <h3 className="section-title">
        {title} <em>{emWord}</em>
      </h3>
    </>
  );

  const renderSectionB = () => (
    <div>
      <SectionHeading id="B" subject="Inventory" title="Data inventory" emWord="& context." />
      
      <div className="question-group">
        <label className="question-label">B1. Nature & Categories of Data</label>
        
        <div className="question-item">
          <p className="question-text">B1.1. What categories of personal data do you collect?</p>
          <div className="checkbox-group">
            {[
              { key: 'B1.1', label: 'Basic identifiers (Name, Phone, Email, Address)' },
              { key: 'B1.2', label: 'Financial data' },
              { key: 'B1.3', label: 'Government IDs (PAN, Aadhaar, Passport)' },
              { key: 'B1.4', label: 'Employment data' },
              { key: 'B1.5', label: 'Health data' },
              { key: 'B1.6', label: 'Biometric data' },
              { key: 'B1.7', label: 'Behavioural data' },
              { key: 'B1.1.8', label: 'Location data' }
            ].map(item => (
              <div key={item.key} className="checkbox-option">
                <input
                  type="checkbox"
                  id={item.key}
                  checked={responses[item.key] || false}
                  onChange={(e) => handleCheckboxChange(item.key, e.target.checked)}
                />
                <label htmlFor={item.key}>{item.label}</label>
              </div>
            ))}
          </div>
        </div>

        <div className="question-item">
          <p className="question-text">B1.8. Do you collect or process data of minors (below 18 years)?</p>
          <div className="radio-group">
            <div className="radio-option">
              <input type="radio" id="B1.8-yes" name="B1.8" checked={getResponseValue('B1.8') === true} onChange={() => handleChange('B1.8', true)} />
              <label htmlFor="B1.8-yes">Yes</label>
            </div>
            <div className="radio-option">
              <input type="radio" id="B1.8-no" name="B1.8" checked={getResponseValue('B1.8') === false} onChange={() => handleChange('B1.8', false)} />
              <label htmlFor="B1.8-no">No</label>
            </div>
          </div>
        </div>

        <div className="question-item">
          <p className="question-text">B1.9. Do you collect sensitive data (financial, health, biometrics, Aadhaar)?</p>
          <div className="radio-group">
            <div className="radio-option">
              <input type="radio" id="B1.9-yes" name="B1.9" checked={getResponseValue('B1.9') === true} onChange={() => handleChange('B1.9', true)} />
              <label htmlFor="B1.9-yes">Yes</label>
            </div>
            <div className="radio-option">
              <input type="radio" id="B1.9-no" name="B1.9" checked={getResponseValue('B1.9') === false} onChange={() => handleChange('B1.9', false)} />
              <label htmlFor="B1.9-no">No</label>
            </div>
          </div>
        </div>

        <div className="question-item">
          <p className="question-text">B1.10. Do you process large volumes of customer or employee data (threshold: >10,000 individuals per year)?</p>
          <div className="radio-group">
            <div className="radio-option">
              <input type="radio" id="B1.10-yes" name="B1.10" checked={getResponseValue('B1.10') === true} onChange={() => handleChange('B1.10', true)} />
              <label htmlFor="B1.10-yes">Yes</label>
            </div>
            <div className="radio-option">
              <input type="radio" id="B1.10-no" name="B1.10" checked={getResponseValue('B1.10') === false} onChange={() => handleChange('B1.10', false)} />
              <label htmlFor="B1.10-no">No</label>
            </div>
          </div>
        </div>

        {(responses['B1.8'] === true || responses['B1.9'] === true) && (
          <div className="conditional-section">
            <div className="question-item">
              <p className="question-text">B1.9A. Do you have a Data Protection Officer (DPO) or equivalent role?</p>
              <div className="radio-group">
                <div className="radio-option">
                  <input type="radio" id="B1.9A-yes" name="B1.9A" checked={getResponseValue('B1.9A') === true} onChange={() => handleChange('B1.9A', true)} />
                  <label htmlFor="B1.9A-yes">Yes</label>
                </div>
                <div className="radio-option">
                  <input type="radio" id="B1.9A-no" name="B1.9A" checked={getResponseValue('B1.9A') === false} onChange={() => handleChange('B1.9A', false)} />
                  <label htmlFor="B1.9A-no">No</label>
                </div>
              </div>
            </div>

            <div className="question-item">
              <p className="question-text">B1.9B. Do you have a formalised data governance framework?</p>
              <div className="radio-group">
                <div className="radio-option">
                  <input type="radio" id="B1.9B-yes" name="B1.9B" checked={getResponseValue('B1.9B') === true} onChange={() => handleChange('B1.9B', true)} />
                  <label htmlFor="B1.9B-yes">Yes</label>
                </div>
                <div className="radio-option">
                  <input type="radio" id="B1.9B-no" name="B1.9B" checked={getResponseValue('B1.9B') === false} onChange={() => handleChange('B1.9B', false)} />
                  <label htmlFor="B1.9B-no">No</label>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderSectionC = () => (
    <div>
      <SectionHeading id="C" subject="Consent" title="Consent, notices" emWord="& rights." />
      
      <div className="question-group">
        <label className="question-label">C1. Consent & Notices</label>
        {[
          { key: 'C1.1', text: 'Do you obtain explicit, itemised consent before collecting personal data?' },
          { key: 'C1.2', text: 'Are consent notices written in simple, plain English or local language?' },
          { key: 'C1.3', text: 'Can individuals easily withdraw consent at any time?' },
          { key: 'C1.4', text: 'Do you maintain consent logs (timestamp, purpose, user identity)?' },
          { key: 'C1.5', text: 'Are notices updated whenever processing purpose changes?' }
        ].map(item => (
          <div key={item.key} className="question-item">
            <p className="question-text">{item.text}</p>
            <div className="radio-group">
              <div className="radio-option">
                <input type="radio" id={`${item.key}-yes`} name={item.key} checked={getResponseValue(item.key) === true} onChange={() => handleChange(item.key, true)} />
                <label htmlFor={`${item.key}-yes`}>Yes</label>
              </div>
              <div className="radio-option">
                <input type="radio" id={`${item.key}-no`} name={item.key} checked={getResponseValue(item.key) === false} onChange={() => handleChange(item.key, false)} />
                <label htmlFor={`${item.key}-no`}>No</label>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="question-group">
        <label className="question-label">C2. Data Principal Rights</label>
        {[
          { key: 'C2.1', text: 'Do you have an established workflow for responding to user rights requests (access, correction, erasure, grievance)?' },
          { key: 'C2.2', text: 'Do you track turnaround times for grievance redressal (requirements: 7–30 days)?' },
          { key: 'C2.3', text: 'Is there a designated Data Protection Manager (DPM) or grievance officer?' }
        ].map(item => (
          <div key={item.key} className="question-item">
            <p className="question-text">{item.text}</p>
            <div className="radio-group">
              <div className="radio-option">
                <input type="radio" id={`${item.key}-yes`} name={item.key} checked={getResponseValue(item.key) === true} onChange={() => handleChange(item.key, true)} />
                <label htmlFor={`${item.key}-yes`}>Yes</label>
              </div>
              <div className="radio-option">
                <input type="radio" id={`${item.key}-no`} name={item.key} checked={getResponseValue(item.key) === false} onChange={() => handleChange(item.key, false)} />
                <label htmlFor={`${item.key}-no`}>No</label>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSectionD = () => (
    <div>
      <SectionHeading id="D" subject="Retention" title="Minimisation" emWord="& retention." />
      
      <div className="question-group">
        <label className="question-label">D1. Minimisation</label>
        {[
          { key: 'D1.1', text: 'Do you collect only data strictly required for your services?' },
          { key: 'D1.2', text: 'Do you prevent over-collection of unnecessary customer information?' }
        ].map(item => (
          <div key={item.key} className="question-item">
            <p className="question-text">{item.text}</p>
            <div className="radio-group">
              <div className="radio-option">
                <input type="radio" id={`${item.key}-yes`} name={item.key} checked={getResponseValue(item.key) === true} onChange={() => handleChange(item.key, true)} />
                <label htmlFor={`${item.key}-yes`}>Yes</label>
              </div>
              <div className="radio-option">
                <input type="radio" id={`${item.key}-no`} name={item.key} checked={getResponseValue(item.key) === false} onChange={() => handleChange(item.key, false)} />
                <label htmlFor={`${item.key}-no`}>No</label>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="question-group">
        <label className="question-label">D2. Retention</label>
        {[
          { key: 'D2.1', text: 'Do you have a data retention policy?' },
          { key: 'D2.2', text: 'Do you securely delete or anonymise data once retention period ends?' },
          { key: 'D2.3', text: 'Do business teams routinely review old data repositories?' }
        ].map(item => (
          <div key={item.key} className="question-item">
            <p className="question-text">{item.text}</p>
            <div className="radio-group">
              <div className="radio-option">
                <input type="radio" id={`${item.key}-yes`} name={item.key} checked={getResponseValue(item.key) === true} onChange={() => handleChange(item.key, true)} />
                <label htmlFor={`${item.key}-yes`}>Yes</label>
              </div>
              <div className="radio-option">
                <input type="radio" id={`${item.key}-no`} name={item.key} checked={getResponseValue(item.key) === false} onChange={() => handleChange(item.key, false)} />
                <label htmlFor={`${item.key}-no`}>No</label>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSectionE = () => (
    <div>
      <SectionHeading id="E" subject="Security" title="Security" emWord="controls." />
      
      <div className="question-group">
        <label className="question-label">E1. Basic Security Hygiene (for all firms)</label>
        {[
          { key: 'E1.1', text: 'Do you use access controls and role-based permissions?' },
          { key: 'E1.2', text: 'Are laptops/desktops encrypted?' },
          { key: 'E1.3', text: 'Do you maintain endpoint security (antivirus, patching, device management)?' },
          { key: 'E1.4', text: 'Are servers and databases encrypted at rest and in transit?' },
          { key: 'E1.5', text: 'Do employees use strong passwords and MFA?' }
        ].map(item => (
          <div key={item.key} className="question-item">
            <p className="question-text">{item.text}</p>
            <div className="radio-group">
              <div className="radio-option">
                <input type="radio" id={`${item.key}-yes`} name={item.key} checked={getResponseValue(item.key) === true} onChange={() => handleChange(item.key, true)} />
                <label htmlFor={`${item.key}-yes`}>Yes</label>
              </div>
              <div className="radio-option">
                <input type="radio" id={`${item.key}-no`} name={item.key} checked={getResponseValue(item.key) === false} onChange={() => handleChange(item.key, false)} />
                <label htmlFor={`${item.key}-no`}>No</label>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="question-group">
        <label className="question-label">E2. Advanced Security (conditional)</label>
        <p className="section-description">If you process financial, health, government ID, or high-volume data → ask these</p>
        {[
          { key: 'E2.1', text: 'Do you conduct periodic vulnerability assessments or penetration tests?' },
          { key: 'E2.2', text: 'Do you have a breach notification and incident-response SOP?' },
          { key: 'E2.3', text: 'Do third-party SaaS tools and cloud providers meet DPDP and security benchmarks (SOC2/ISO27001)?' },
          { key: 'E2.4', text: 'Do you have network monitoring and logging tools?' }
        ].map(item => (
          <div key={item.key} className="question-item">
            <p className="question-text">{item.text}</p>
            <div className="radio-group">
              <div className="radio-option">
                <input type="radio" id={`${item.key}-yes`} name={item.key} checked={getResponseValue(item.key) === true} onChange={() => handleChange(item.key, true)} />
                <label htmlFor={`${item.key}-yes`}>Yes</label>
              </div>
              <div className="radio-option">
                <input type="radio" id={`${item.key}-no`} name={item.key} checked={getResponseValue(item.key) === false} onChange={() => handleChange(item.key, false)} />
                <label htmlFor={`${item.key}-no`}>No</label>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSectionF = () => (
    <div>
      <SectionHeading id="F" subject="Vendors" title="Third-party" emWord="& SaaS." />
      
      <div className="question-group">
        {[
          { key: 'F1', text: 'Do you outsource any part of your operations to vendors (CRM, payroll, billing, cloud, marketing tools)?' },
          { key: 'F2', text: 'Do these vendors store or process personal data?' },
          { key: 'F3', text: 'Do you have DPDP-aligned Data Sharing Agreements (DSA) with these vendors?' },
          { key: 'F4', text: 'Do you audit vendors annually for compliance?' },
          { key: 'F5', text: 'Do you use any free or trial SaaS tools that store customer data?' }
        ].map(item => (
          <div key={item.key} className="question-item">
            <p className="question-text">{item.text}</p>
            <div className="radio-group">
              <div className="radio-option">
                <input type="radio" id={`${item.key}-yes`} name={item.key} checked={getResponseValue(item.key) === true} onChange={() => handleChange(item.key, true)} />
                <label htmlFor={`${item.key}-yes`}>Yes</label>
              </div>
              <div className="radio-option">
                <input type="radio" id={`${item.key}-no`} name={item.key} checked={getResponseValue(item.key) === false} onChange={() => handleChange(item.key, false)} />
                <label htmlFor={`${item.key}-no`}>No</label>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSectionG = () => (
    <div>
      <SectionHeading id="G" subject="Transfers" title="Cross-border" emWord="transfers." />
      
      <div className="question-group">
        {[
          { key: 'G1', text: 'Do you use cloud tools hosted outside India (Google, AWS, Zoho, HubSpot, Notion, etc.)?' },
          { key: 'G2', text: 'Do these tools transfer user or employee data internationally?' },
          { key: 'G3', text: 'Do you have safeguards or contractual clauses for such transfers?' }
        ].map(item => (
          <div key={item.key} className="question-item">
            <p className="question-text">{item.text}</p>
            <div className="radio-group">
              <div className="radio-option">
                <input type="radio" id={`${item.key}-yes`} name={item.key} checked={getResponseValue(item.key) === true} onChange={() => handleChange(item.key, true)} />
                <label htmlFor={`${item.key}-yes`}>Yes</label>
              </div>
              <div className="radio-option">
                <input type="radio" id={`${item.key}-no`} name={item.key} checked={getResponseValue(item.key) === false} onChange={() => handleChange(item.key, false)} />
                <label htmlFor={`${item.key}-no`}>No</label>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSectionH = () => (
    <div>
      <SectionHeading id="H" subject="Awareness" title="Awareness" emWord="& training." />
      
      <div className="question-group">
        {[
          { key: 'H1', text: 'Do employees receive annual DPDP compliance training?' },
          { key: 'H2', text: 'Are staff aware of phishing, social engineering, and data leakage risks?' },
          { key: 'H3', text: 'Do you maintain logs and evidence of training completion?' }
        ].map(item => (
          <div key={item.key} className="question-item">
            <p className="question-text">{item.text}</p>
            <div className="radio-group">
              <div className="radio-option">
                <input type="radio" id={`${item.key}-yes`} name={item.key} checked={getResponseValue(item.key) === true} onChange={() => handleChange(item.key, true)} />
                <label htmlFor={`${item.key}-yes`}>Yes</label>
              </div>
              <div className="radio-option">
                <input type="radio" id={`${item.key}-no`} name={item.key} checked={getResponseValue(item.key) === false} onChange={() => handleChange(item.key, false)} />
                <label htmlFor={`${item.key}-no`}>No</label>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSectionI = () => (
    <div>
      <SectionHeading id="I" subject="Governance" title="Governance" emWord="& docs." />
      
      <div className="question-group">
        <label className="question-label">I1. Do you maintain the following documents?</label>
        {[
          { key: 'I1.1', text: 'Privacy Policy' },
          { key: 'I1.2', text: 'Data Retention Policy' },
          { key: 'I1.3', text: 'Consent Management Policy' },
          { key: 'I1.4', text: 'Breach Management SOP' },
          { key: 'I1.5', text: 'Vendor Risk Policy' },
          { key: 'I1.6', text: 'Data Accuracy & Correction SOP' }
        ].map(item => (
          <div key={item.key} className="question-item">
            <p className="question-text">{item.text}</p>
            <div className="radio-group">
              <div className="radio-option">
                <input type="radio" id={`${item.key}-yes`} name={item.key} checked={getResponseValue(item.key) === true} onChange={() => handleChange(item.key, true)} />
                <label htmlFor={`${item.key}-yes`}>Yes</label>
              </div>
              <div className="radio-option">
                <input type="radio" id={`${item.key}-no`} name={item.key} checked={getResponseValue(item.key) === false} onChange={() => handleChange(item.key, false)} />
                <label htmlFor={`${item.key}-no`}>No</label>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="question-group">
        {[
          { key: 'I2', text: 'Is top management aware of DPDP obligations and penalties?' },
          { key: 'I3', text: 'Do you have Board-level or CXO-level oversight for privacy?' }
        ].map(item => (
          <div key={item.key} className="question-item">
            <p className="question-text">{item.text}</p>
            <div className="radio-group">
              <div className="radio-option">
                <input type="radio" id={`${item.key}-yes`} name={item.key} checked={getResponseValue(item.key) === true} onChange={() => handleChange(item.key, true)} />
                <label htmlFor={`${item.key}-yes`}>Yes</label>
              </div>
              <div className="radio-option">
                <input type="radio" id={`${item.key}-no`} name={item.key} checked={getResponseValue(item.key) === false} onChange={() => handleChange(item.key, false)} />
                <label htmlFor={`${item.key}-no`}>No</label>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSectionJ = () => (
    <div>
      <SectionHeading id="J" subject="High Risk" title="Sector-specific" emWord="questions." />
      <p className="section-description">Only applicable if the firm deals with specific data categories.</p>
      
      <div className="question-group">
        <label className="question-label">J1. Financial Services (Wealth mgmt, NBFC, Insurance, FinTech)</label>
        {[
          { key: 'J1.1', text: 'Do you store customer financial data (bank accounts, investments, tax records)?' },
          { key: 'J1.2', text: 'Do you store KYC data (PAN, Aadhaar, CKYC)?' },
          { key: 'J1.3', text: 'Do you store nominee information and family details?' },
          { key: 'J1.4', text: 'Do you use external intermediaries (RTA, KRAs, brokers)?' },
          { key: 'J1.5', text: 'Do you have transaction monitoring and audit logs?' }
        ].map(item => (
          <div key={item.key} className="question-item">
            <p className="question-text">{item.text}</p>
            <div className="radio-group">
              <div className="radio-option">
                <input type="radio" id={`${item.key}-yes`} name={item.key} checked={getResponseValue(item.key) === true} onChange={() => handleChange(item.key, true)} />
                <label htmlFor={`${item.key}-yes`}>Yes</label>
              </div>
              <div className="radio-option">
                <input type="radio" id={`${item.key}-no`} name={item.key} checked={getResponseValue(item.key) === false} onChange={() => handleChange(item.key, false)} />
                <label htmlFor={`${item.key}-no`}>No</label>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="question-group">
        <label className="question-label">J2. Healthcare Firms (Clinics, Labs, Telemedicine)</label>
        {[
          { key: 'J2.1', text: 'Do you store medical reports, diagnostics, prescriptions, EHR?' },
          { key: 'J2.2', text: 'Do you share data with diagnostic partners or labs?' },
          { key: 'J2.3', text: 'Do you use WhatsApp or email to share patient data?' }
        ].map(item => (
          <div key={item.key} className="question-item">
            <p className="question-text">{item.text}</p>
            <div className="radio-group">
              <div className="radio-option">
                <input type="radio" id={`${item.key}-yes`} name={item.key} checked={getResponseValue(item.key) === true} onChange={() => handleChange(item.key, true)} />
                <label htmlFor={`${item.key}-yes`}>Yes</label>
              </div>
              <div className="radio-option">
                <input type="radio" id={`${item.key}-no`} name={item.key} checked={getResponseValue(item.key) === false} onChange={() => handleChange(item.key, false)} />
                <label htmlFor={`${item.key}-no`}>No</label>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="question-group">
        <label className="question-label">J3. HR, Payroll, Staffing</label>
        {[
          { key: 'J3.1', text: 'Do you store salary slips, PF/ESI, bank details?' },
          { key: 'J3.2', text: 'Do you handle background verification data?' },
          { key: 'J3.3', text: 'Do you track employee attendance/location digitally?' }
        ].map(item => (
          <div key={item.key} className="question-item">
            <p className="question-text">{item.text}</p>
            <div className="radio-group">
              <div className="radio-option">
                <input type="radio" id={`${item.key}-yes`} name={item.key} checked={getResponseValue(item.key) === true} onChange={() => handleChange(item.key, true)} />
                <label htmlFor={`${item.key}-yes`}>Yes</label>
              </div>
              <div className="radio-option">
                <input type="radio" id={`${item.key}-no`} name={item.key} checked={getResponseValue(item.key) === false} onChange={() => handleChange(item.key, false)} />
                <label htmlFor={`${item.key}-no`}>No</label>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="question-group">
        <label className="question-label">J4. EdTech</label>
        {[
          { key: 'J4.1', text: 'Do you store student data and parental data?' },
          { key: 'J4.2', text: 'Do you use behavioural analytics tools?' }
        ].map(item => (
          <div key={item.key} className="question-item">
            <p className="question-text">{item.text}</p>
            <div className="radio-group">
              <div className="radio-option">
                <input type="radio" id={`${item.key}-yes`} name={item.key} checked={getResponseValue(item.key) === true} onChange={() => handleChange(item.key, true)} />
                <label htmlFor={`${item.key}-yes`}>Yes</label>
              </div>
              <div className="radio-option">
                <input type="radio" id={`${item.key}-no`} name={item.key} checked={getResponseValue(item.key) === false} onChange={() => handleChange(item.key, false)} />
                <label htmlFor={`${item.key}-no`}>No</label>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="question-group">
        <label className="question-label">J5. E-commerce / Retail</label>
        {[
          { key: 'J5.1', text: 'Do you store delivery addresses and payment logs?' },
          { key: 'J5.2', text: 'Do you store browsing and purchase patterns?' }
        ].map(item => (
          <div key={item.key} className="question-item">
            <p className="question-text">{item.text}</p>
            <div className="radio-group">
              <div className="radio-option">
                <input type="radio" id={`${item.key}-yes`} name={item.key} checked={getResponseValue(item.key) === true} onChange={() => handleChange(item.key, true)} />
                <label htmlFor={`${item.key}-yes`}>Yes</label>
              </div>
              <div className="radio-option">
                <input type="radio" id={`${item.key}-no`} name={item.key} checked={getResponseValue(item.key) === false} onChange={() => handleChange(item.key, false)} />
                <label htmlFor={`${item.key}-no`}>No</label>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const sections = [
    { id: 'B', label: 'Section B', render: renderSectionB },
    { id: 'C', label: 'Section C', render: renderSectionC },
    { id: 'D', label: 'Section D', render: renderSectionD },
    { id: 'E', label: 'Section E', render: renderSectionE },
    { id: 'F', label: 'Section F', render: renderSectionF },
    { id: 'G', label: 'Section G', render: renderSectionG },
    { id: 'H', label: 'Section H', render: renderSectionH },
    { id: 'I', label: 'Section I', render: renderSectionI },
    { id: 'J', label: 'Section J', render: renderSectionJ }
  ];

  return (
    <div className="questionnaire-container">
      <div className="section-title-eyebrow">
        <span>Detailed Assessment</span>
        <span className="dot">◆</span>
        <span>Sections B–J</span>
      </div>
      <h2 className="section-title">
        Detailed <em>assessment.</em>
      </h2>
      <p className="section-description">
        Walk through each section to capture how personal data is handled across your business.
        Your responses are auto-saved as you move between sections.
      </p>

      <div style={{ marginBottom: '2rem', maxWidth: '480px' }}>
        <label
          htmlFor="organizationName"
          style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontFamily: 'var(--mono)',
            fontSize: '11px',
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--muted)',
            fontWeight: 500
          }}
        >
          Organization Name (Optional)
        </label>
        <input
          id="organizationName"
          type="text"
          className="organization-input"
          placeholder="e.g. Acme Pvt. Ltd."
          value={organizationName}
          onChange={(e) => setOrganizationName(e.target.value)}
        />
      </div>

      <div className="section-nav">
        {sections.map(section => (
          <button
            key={section.id}
            type="button"
            onClick={() => handleSectionChange(section.id)}
            className={`section-nav-btn ${currentSection === section.id ? 'active' : ''}`}
          >
            {section.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit}>
        {sections.find(s => s.id === currentSection)?.render()}

        {currentSection === 'J' && (
          <div className="button-group">
            <button type="button" className="btn btn-secondary" onClick={() => handleSectionChange('I')}>
              ← Previous
            </button>
            <button type="submit" className="btn btn-success" disabled={loading}>
              {loading ? 'Submitting…' : 'Submit Assessment'} <span className="arrow">→</span>
            </button>
          </div>
        )}

        {currentSection !== 'B' && currentSection !== 'J' && (
          <div className="button-group">
            <button type="button" className="btn btn-secondary" onClick={() => {
              const currentIndex = sections.findIndex(s => s.id === currentSection);
              if (currentIndex > 0) handleSectionChange(sections[currentIndex - 1].id);
            }}>
              ← Previous
            </button>
            <button type="button" className="btn btn-primary" onClick={() => {
              const currentIndex = sections.findIndex(s => s.id === currentSection);
              if (currentIndex < sections.length - 1) handleSectionChange(sections[currentIndex + 1].id);
            }}>
              Next Section <span className="arrow">→</span>
            </button>
          </div>
        )}

        {currentSection === 'B' && (
          <div className="button-group">
            <button type="button" className="btn btn-primary" onClick={() => handleSectionChange('C')}>
              Next Section <span className="arrow">→</span>
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default SectionB;

